import * as Yup from "yup";

// Step 1: Basic Information
export const basicInformationSchema = Yup.object().shape({
  profilePhoto: Yup.string().required("Profile photo is required"),
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .matches(/^[A-Za-z\s]+$/, "Name must not contain numbers")
    .required("Full Name is required"),
  fatherName: Yup.string()
    .min(2, "Father Name must be at least 2 characters")
    .matches(/^[A-Za-z\s]+$/, "Father Name must not contain numbers")
    .required("Father Name is required"),
  cnicNo: Yup.string()
    .matches(
      /^\d{5}-\d{7}-\d{1}$/,
      "CNIC must be in the format XXXXX-XXXXXXX-X"
    )
    .required("CNIC is required"),
  mobileNumber: Yup.string()
    .matches(/^\d{9}$/, "Mobile Number must be exactly 9 digits after 03")
    .required("Mobile Number is required"),
  gender: Yup.string().required("Gender is required"),
  maritalStatus: Yup.string().required("Marital Status is required"),
  cnicFront: Yup.mixed().required("CNIC front image is required"),
  cnicBack: Yup.mixed().required("CNIC back image is required"),
});

// Step 2: Personal Details
export const personalInfoSchema = Yup.object({
  currentAddress: Yup.string().required("Current Address is required"),
  permanentAddress: Yup.string().required("Permanent Address is required"),
  tenantType: Yup.string().required("Please select occupancy type"),
  totalFamilyMembers: Yup.number()
    .min(1, "At least 1 member is required")
    .required("Total members is required"),
  language: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one language")
    .required("Language is required"),
  cast: Yup.string().required("Cast/Community is required"),
});

// Step 3: Employment & Financial
export const employmentSchema = Yup.object({
  employmentStatus: Yup.string().required("Employment status is required"),
  companyName: Yup.string().when("employmentStatus", {
    is: (val) => val === "employed" || val === "self_employed",
    then: () => Yup.string().required("Company/Business name is required"),
    otherwise: () => Yup.string(),
  }),
  designation: Yup.string().when("employmentStatus", {
    is: "employed",
    then: () => Yup.string().required("Designation is required"),
    otherwise: () => Yup.string(),
  }),
  instituteName: Yup.string().when("employmentStatus", {
    is: "student",
    then: () => Yup.string().required("Institution name is required"),
    otherwise: () => Yup.string(),
  }),
  monthlyIncome: Yup.string().required("Please select income range"),
});

// Step 4: Lifestyle & Preferences - UPDATED
export const lifestyleSchema = Yup.object({
  hasPets: Yup.boolean(),
  pets: Yup.array().when("hasPets", ([hasPets], schema) => {
    return hasPets
      ? schema
          .of(
            Yup.object().shape({
              type: Yup.string().required("Pet type is required"),
              count: Yup.number()
                .min(1, "Pet count must be at least 1")
                .required("Pet count is required"),
            })
          )
          .min(1, "Please add at least one pet")
      : schema;
  }),
  hasVehicle: Yup.boolean(),
  vehicles: Yup.array().when("hasVehicle", ([hasVehicle], schema) => {
    return hasVehicle
      ? schema
          .of(
            Yup.object().shape({
              type: Yup.string()
                .oneOf(["bike", "car"], "Vehicle type must be bike or car")
                .required("Vehicle type is required"),
              number: Yup.string()
                .matches(
                  /^[A-Z]{3}-\d{3,4}$/,
                  "Vehicle number must be in format ABC-123 or ABC-1234"
                )
                .required("Vehicle number is required"),
            })
          )
          .min(1, "Please add at least one vehicle")
      : schema;
  }),
  preferredMoveIn: Yup.string().required("Please select move-in timeline"),
  budgetRange: Yup.array().of(Yup.number()),
});

// Step 5: Additional Info
export const additionalInfoSchema = Yup.object({
  emergencyContactName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Name must not contain numbers")
    .required("Emergency contact name is required!"),
  emergencyContactRelation: Yup.string().required("Relationship is required"),
  emergencyContactPhone: Yup.string()
    .matches(/^\d{9}$/, "Phone must be exactly 9 digits after 03")
    .required("Emergency contact phone is required"),
  aboutMe: Yup.string().max(300, "About me must be less than 300 characters"),
  hasPreviousRental: Yup.boolean(),
  previousLandlordName: Yup.string(),
  previousLandlordPhone: Yup.string(),
  // Digital Signature
  signature: Yup.string().required("Digital signature is required"),
});

// Combined schema for final validation
export const fullRegistrationSchema = Yup.object().shape({
  // Basic Info
  name: Yup.string().required(),
  fatherName: Yup.string().required(),
  cnicNo: Yup.string().required(),
  mobileNumber: Yup.string().required(),
  gender: Yup.mixed().required(),
  maritalStatus: Yup.string().required(),

  // Personal Details
  currentAddress: Yup.string().required(),
  permanentAddress: Yup.string().required(),
  tenantType: Yup.string().required(),
  totalFamilyMembers: Yup.number().min(1).required(),
  language: Yup.array().min(1).required(),
  cast: Yup.string().required(),

  // Employment
  employmentStatus: Yup.string().required(),
  monthlyIncome: Yup.string().required(),

  // Lifestyle
  preferredMoveIn: Yup.string().required(),

  // Additional
  emergencyContactRelation: Yup.string().required(),
  emergencyContactPhone: Yup.string().required(),
  signature: Yup.string().required(),
});
